import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { UserTimingService } from '../../../core/services/user-timing.service';
import { HttpErrorResponse } from '@angular/common/http';
import { InboundCall } from '../../../core/models/inbound-call.model';
import { InboundCallsService } from '../../../core/services/inbound-calls.service';
import { CaptureSearchService, PagedCaptureSearchResults } from '../../../core/services/capture-search.service';
import { CaptureSearchParamSet } from '../../../core/models/capture-search-param-set.model';
import { keyedReasonOptions } from '../../../core/options/inbound-call-reasons.opts';
import { DatePipe } from '@angular/common';
import { PatientNameComponent } from '../../../shared/components/patient-name/patient-name.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';
import { OfficeComponent } from '../../../shared/components/office/office.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BlankValueDashPipe } from '../../../shared/pipes/blank-value-dash.pipe';
import { CaptureStatusPipe } from '../../../shared/pipes/capture-status.pipe';

const shouldReloadInboundCall = (inboundCall: InboundCall, inboundCallId: string) =>
  inboundCall && inboundCallId !== inboundCall.id.toString();

@Component({
    selector: 'app-capture-admin-inbound-call',
    templateUrl: './capture-admin-inbound-call.component.html',
    styleUrls: ['./capture-admin-inbound-call.component.scss'],
    imports: [PatientNameComponent,
              PrescriberNameComponent,
              OfficeComponent,
              MatProgressSpinner,
              DatePipe,
              BlankValueDashPipe,
              CaptureStatusPipe,
              RouterLink]
})
export class CaptureAdminInboundCallComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private inboundCallsService = inject(InboundCallsService);
  private captureSearchService = inject(CaptureSearchService);
  private router = inject(Router);
  private titleService = inject(Title);
  private userTimingService = inject(UserTimingService);

  keyedReasonOptions = keyedReasonOptions;
  inboundCall: InboundCall;
  refreshing = true;
  loadingCaptures = true
  captureParams = new CaptureSearchParamSet();
  paginatedCaptures: PagedCaptureSearchResults;

  constructor() {
    this.route.paramMap.subscribe(paramsMap => {
      this.handleRouteChange(paramsMap);
    });
  }

  ngOnInit() {
    const inboundCallId = this.route.snapshot.paramMap.get('id');
    this.loadCall(inboundCallId);
  }

  ngOnDestroy(): void {
    this.userTimingService.stop();
  }

  private handleRouteChange(paramsMap) {
    const params = paramsMap['params'];
    const inboundCallId = params && params['id'];

    if (shouldReloadInboundCall(this.inboundCall, inboundCallId)) {
      this.loadCall(inboundCallId);
    }
  }

  private loadCall(inboundCallId: string): void {
    this.refreshing = true;
    this.inboundCallsService.get(inboundCallId).subscribe(
      inboundCall => {
        this.inboundCall = inboundCall;
        this.captureParams.patientId = inboundCall.patient.id.toString();
        this.captureParams.prescriberNpi = inboundCall.provider.npi;
        this.refreshing = false;
        this.updateTitle();
        this.startTimeTracking();
        this.loadCaptures();
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        } else {
          console.error(err);
        }
      }
    );
    window.scrollTo(0, 0);
  }

  private loadCaptures(): void {
    this.captureSearchService.search(this.captureParams).subscribe(
      result => {
        console.log(result)
        this.paginatedCaptures = result;
        this.loadingCaptures = false;
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        } else {
          console.error(err);
        }
      }
    );
    window.scrollTo(0, 0);
  }

  private updateTitle() {
    const titleDetail = this.inboundCall.patient ? this.inboundCall.patient.mrn : this.inboundCall.client.humanizedName;
    const title = `NuvemRx 340B Recovery - View Inbound Call | ${titleDetail}`;
    this.titleService.setTitle(title);
  }

  private startTimeTracking() {
    this.userTimingService.track({
      page: 'inbound_call_details',
      recordId: this.inboundCall.id,
      reason: this.inboundCall.reason,
    });
  }
}
